/* eslint-disable no-restricted-globals */
import React, { useMemo, useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';

import {
  createStyles,
  makeStyles,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Theme,
  Paper,
  TableBody,
  Link,
  Box,
  Dialog,
  DialogContent,
  IconButton,
} from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CloseIcon from '@material-ui/icons/Close';
import { Typography } from '@cbreone/core-ui';
import ExpandableText from '../../components/ExpandableText';
import { BHPropertyFieldRule } from '../../types/bh-property-field-rule';
import { AVAILABILITY_NAME_KEY, linkAttributes, linkAttributes_pac, linkAttributes_UK_Regional, Measurement } from '../../data/constants';
import { Survey } from '../../types';
import { getColumnHeader } from '../../utilities';
import { getAvailabilityNameHeader, isAvailabilityNameApplicable, getAvailabilityFieldValues } from '../../utilities/property/propertyUtil';
import { calculateSubEntityTotals, extractUnit, formatNumericValue } from '../../utilities/property/tableCalculations';
import { CountriesWithCE, CountriesWithPAC, CountryKeys, CountriesWithTotals } from '../../map/constants/MapStyles';

type AvailabilityTableVariant = 'auto' | 'simple' | 'subEntity';
type ResolvedAvailabilityTableVariant = 'simple' | 'subEntity';

type AvailabilityFloorPlan = {
  id?: number;
  url?: string;
  pdf?: string;
  downloadUrl?: string;
  displayName?: string;
  displayCaption?: string;
};

export type Props = {
  survey: Survey;
  displayFields: BHPropertyFieldRule[];
  availabilities: any[];
  isCESurvey: boolean;
  templateData?: any; // Template configuration for nested tables
  tableVariant?: AvailabilityTableVariant;
  floorPlans?: AvailabilityFloorPlan[];
};

const isAvailabilityDescriptionField = (fieldKey?: string) => {
  const normalizedFieldKey = `${fieldKey || ''}`.toLowerCase();
  return normalizedFieldKey.startsWith('availability_info')
    && normalizedFieldKey.includes('description');
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      fontFamily: 'Calibre-R',
      fontSize: '14px',
      fontWeight: 400,
      wordWrap: 'break-word',
      '&:nth-of-type(even)': {
        backgroundColor: '#fbfcfc',

        '& .MuiTableCell-root:first-of-type': {
          background: '#e6eaea',
        },
      },
      '&:nth-of-type(odd)': {
        '& .MuiTableCell-root:first-of-type': {
          background: '#F5F7F7',
        },
        background: theme.palette.common.white,
      },
      verticalAlign: 'top',
    },
    availabilitiesText: {
      color: theme.palette.primary.main,
      fontFamily: 'Financier Display, Georgia, serif',
      fontSize: '28px',
      fontWeight: 400,
      lineHeight: '36px',
      marginBottom: '12px',
    },
    paperRoot: {
      boxShadow: 'none',
      marginTop: '10px',
      maxWidth: '100%',
      overflow: 'visible',
      borderRadius: 0,
    },
    tableRoot: {
      borderCollapse: 'separate',
      borderSpacing: 0,
      '& .MuiTableCell-root': {
        borderBottom: '1px solid #DFE6E5',
      },
    },
    stickyColumn: {
      position: 'sticky',
      zIndex: 3,
      left: '0px',
      background: '#e6eaea',
      width: 'auto',
      minWidth: '220px',
      maxWidth: 'none',
    },
    stickyColumnHeader: {
      position: 'sticky',
      zIndex: 3,
      left: '0px',
      background: '#F5F7F7',
      width: 'auto',
      minWidth: '220px',
      maxWidth: 'none',
    },
    tableHeader: {
      whiteSpace: 'pre-wrap',
      background: '#F5F7F7',
      fontFamily: 'Calibre-R',
      fontSize: '14px',
      fontWeight: 500,
      '&>.MuiTableCell-root': {
        borderBottom: '1px solid #DFE6E5',
      },
    },
    nestedParentRow: {
      backgroundColor: '#FFFFFF',
      '& .MuiTableCell-root': {
        borderBottom: 'none',
      },
    },
    nestedWrapperRow: {
      backgroundColor: '#FFFFFF',
      '& .MuiTableCell-root': {
        borderBottom: '1px solid #EEF0F0',
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    nestedWrapperCell: {
      padding: 0,
      borderBottom: '1px solid #DFE6E5',
    },
    nestedPanel: {
      margin: 0,
      padding: '16px 0',
      backgroundColor: '#FFFFFF',
      boxSizing: 'border-box',
      borderLeft: '4px solid #FFFFFF',
    },
    nestedTable: {
      width: '100%',
      backgroundColor: '#FFFFFF',
      borderCollapse: 'collapse',
      '& th': {
        padding: '12px 16px',
        textAlign: 'left',
        fontFamily: 'Calibre-R',
        fontSize: '13px',
        fontWeight: 700,
        lineHeight: '18px',
        borderBottom: '2px solid #DFE6E5',
        backgroundColor: '#F9FAFA',
        color: '#333333',
        textTransform: 'none',
        letterSpacing: '0.3px',
      },
      '& td': {
        padding: '12px 16px',
        fontFamily: 'Calibre-R',
        fontSize: '13px',
        lineHeight: '18px',
        borderBottom: '1px solid #EEF0F0',
        verticalAlign: 'middle',
        color: '#333333',
      },
      '& tbody tr': {
        transition: 'background-color 150ms ease',
        '&:hover': {
          backgroundColor: '#F5F5F5',
        },
      },
      '& tr:last-child td': {
        borderBottom: '1px solid #DFE6E5',
      },
    },
    availabilityNameCell: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    availabilityAccordionToggle: {
      width: '22px',
      height: '22px',
      border: 0,
      padding: 0,
      margin: 0,
      background: 'transparent',
      color: theme.palette.text.primary,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    availabilityChevron: {
      width: '18px',
      height: '18px',
      color: theme.palette.text.primary,
      flexShrink: 0,
      transition: 'transform 160ms ease',
    },
    availabilityChevronExpanded: {
      transform: 'rotate(90deg)',
    },
    statusChip: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '22px',
      padding: '2px 10px',
      borderRadius: '999px',
      fontFamily: 'Calibre-R',
      fontSize: '14px',
      lineHeight: '18px',
      whiteSpace: 'nowrap',
    },
    statusChipAvailable: {
      backgroundColor: '#D8EFE5',
      color: '#004D38',
    },
    statusChipFuture: {
      backgroundColor: '#E8E0FA',
      color: '#3D257C',
    },
    statusChipPending: {
      backgroundColor: '#FFE4C7',
      color: '#995000',
    },
    noBottomBorder: {
      zIndex: 2,
      borderBottom: 'none',
      color: theme.palette.text.primary,
      fontFamily: 'Calibre-R',
      whiteSpace: 'pre-line',
      width: '134px',
      maxWidth: '134px',
      padding: '6px 10px',
      boxSizing: 'content-box',
      wordBreak: 'break-word',
      minWidth: '134px',
      verticalAlign: 'top',
      '&.MuiTableCell-root': {
        fontSize: '14px',
      },
      '&:first-of-type': {
        width: 'auto',
        maxWidth: 'none',
        minWidth: '220px',
        zIndex: 2,
      },

      '@media(max-width:768px)': {
        width: '86px',
        maxWidth: '86px',
        minWidth: '86px',

        '&:first-of-type': {
          width: 'auto',
          maxWidth: 'none',
          minWidth: '220px',
        },
      },
    },
    contentStyle: {
      '& p': {
        marginTop: '0.5em',
        marginBottom: '0.5em',
      },
      '& p:first-child': {
        marginTop: '0em',
      },
      '& p:has(> br:only-child)': {
        marginTop: '-0.5em',
        marginBottom: '-0.5em',
      },
    },
    link: {
      wordWrap: 'break-word',
      fontSize: '14px',
    },
    floorPlanAction: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      width: '112px',
      height: '36px',
      padding: '0 12px',
      border: `1px solid ${theme.palette.primary.main}`,
      borderRadius: '3px',
      color: theme.palette.primary.main,
      fontFamily: 'Calibre-R',
      fontSize: '14px',
      lineHeight: '18px',
      fontWeight: 600,
      textDecoration: 'none',
      whiteSpace: 'nowrap',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      boxSizing: 'border-box',
      '&:hover': {
        textDecoration: 'none',
        backgroundColor: '#F5F7F7',
      },
    },
    floorPlanCarouselHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 20px 8px',
      borderBottom: '1px solid #DFE6E5',
    },
    floorPlanCarouselTitle: {
      color: theme.palette.primary.main,
      fontFamily: 'Calibre-R',
      fontSize: '18px',
      lineHeight: '24px',
      fontWeight: 500,
    },
    floorPlanCarouselContent: {
      padding: '20px',
    },
    floorPlanPreviewFrame: {
      width: '100%',
      minHeight: '520px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#F5F7F7',
      overflow: 'hidden',
      '@media(max-width:768px)': {
        minHeight: '360px',
      },
    },
    floorPlanPreviewImage: {
      maxWidth: '100%',
      maxHeight: '70vh',
      objectFit: 'contain',
    },
    floorPlanPreviewPdf: {
      width: '100%',
      height: '70vh',
      minHeight: '520px',
      border: 0,
      '@media(max-width:768px)': {
        minHeight: '360px',
      },
    },
    floorPlanCarouselControls: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '16px',
      paddingTop: '16px',
    },
    floorPlanCarouselCount: {
      fontFamily: 'Calibre-R',
      fontSize: '14px',
      lineHeight: '20px',
      minWidth: '48px',
      textAlign: 'center',
      color: theme.palette.text.primary,
    },
  }),
);

const PropertyAvailabilities: React.FC<Props> = ({
  survey,
  displayFields,
  availabilities,
  isCESurvey,
  templateData,
  tableVariant = 'auto',
  floorPlans = [],
}) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const [floorPlanCarouselIndex, setFloorPlanCarouselIndex] = useState(0);
  const [isFloorPlanCarouselOpen, setIsFloorPlanCarouselOpen] = useState(false);
  const [expandedAvailabilityKey, setExpandedAvailabilityKey] = useState<string | null>(null);
  const surveyCountry_PAC = CountriesWithPAC.includes(survey.surveyCountry);
  const surveyCountry_CE: boolean = CountriesWithCE.includes(survey.surveyCountry);
  const surveyCountry_UK: boolean = survey.surveyCountry === CountryKeys.UK;
  const containerRef: any = useRef();
  const displayAvailabilities = useMemo(
    () =>
      [...availabilities]
        .filter((availability) => availability.status === 'ACTIVE')
        .sort((first: any, second: any) => first.order - second.order) || [],
    [availabilities],
  );
  const hasFloorPlans = floorPlans.length > 0;
  const displayColumnCount = displayFields.length + (hasFloorPlans ? 1 : 0);
  const availabilityNameHeader = useMemo(
    () => getAvailabilityNameHeader(survey?.surveyCountry), [survey?.surveyCountry]);

  const isIncludeAvailabilityName = useMemo(
    () => isAvailabilityNameApplicable(survey?.surveyCountry, `${survey?.propertyType}`),
    [survey?.surveyCountry, survey?.propertyType]);

  // Check if survey country should show totals (NL/FIN/AT markets)
  const showTotals = useMemo(() => {
    const surveyCountry = survey?.surveyCountry;
    return CountriesWithTotals.includes(surveyCountry);
  }, [survey?.surveyCountry]);

  function getAvailabilityNameValueLocal(availability: any) {
    const name = availability[AVAILABILITY_NAME_KEY]?.replace(/, /g, ',\r\n') || '-';
    return name;
  }

  // Helper function to get sub-entity (nested table) data for an availability
  const getSubEntityData = (availability: any, parentFields: any[]) => {
    // If templateData is provided, use it (survey-lib style)
    if (templateData?.sections && availability?.data) {
      const subEntityResults: any[] = [];

      // Find sections with sectionType: "table" (these are sub-entities)
      const tableSections = templateData.sections.filter((section: any) => section.sectionType === 'table');

      tableSections.forEach((section: any) => {
        const subEntityKey = section.sectionKey;
        const subEntityData = availability.data[subEntityKey];

        if (subEntityData && Array.isArray(subEntityData)) {
          // Get fields for this sub-entity from template
          const subEntityFields = section.columns || [];

          // Create a Set of field keys that belong to the sub-entity
          const subEntityFieldKeys = new Set(
            subEntityFields
              .filter((f: any) => f.status === 'ACTIVE')
              .map((f: any) => f.fieldKey || f.field),
          );

          const filteredData = subEntityData
            .filter((item: any) => !item.status || item.status === 'ACTIVE')
            .sort((a: any, b: any) => (a.order || 0) - (b.order || 0));

          subEntityResults.push({
            entityName: section.sectionName || section.sectionKey,
            subEntityFields: subEntityFields
              .filter((f: any) => f.status === 'ACTIVE')
              .sort((a: any, b: any) => a.order - b.order),
            parentFields, // Include all parent availability fields
            data: filteredData,
            availability, // Pass parent availability for merged cell values
            subEntityFieldKeys, // Set of field keys that belong to sub-entity
            allColumns: subEntityFields, // Keep unfiltered columns for checking field status
          });
        }
      });

      return subEntityResults.length > 0 ? subEntityResults : null;
    }

    // Fallback: Auto-detect nested array data in availability.data
    if (availability?.data) {
      const subEntityResults: any[] = [];
      const parentFieldKeys = new Set(parentFields.map((f) => f.fieldKey));

      // Look for array properties in availability.data
      Object.keys(availability.data).forEach((key) => {
        const value = availability.data[key];

        // Check if this is an array and not a parent field
        if (Array.isArray(value) && value.length > 0 && !parentFieldKeys.has(key)) {
          // Get field keys from the first item in the array
          const firstItem = value[0];
          if (firstItem && typeof firstItem === 'object') {
            const subEntityFieldKeys = new Set(Object.keys(firstItem).filter((k) => k !== 'status' && k !== 'order'));

            // Create field definitions from the data
            const subEntityFields = Array.from(subEntityFieldKeys).map((fieldKey, index) => ({
              field: fieldKey,
              fieldLabel: fieldKey.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
              order: index,
              status: 'ACTIVE',
            }));

            const filteredData = value
              .filter((item: any) => !item.status || item.status === 'ACTIVE')
              .sort((a: any, b: any) => (a.order || 0) - (b.order || 0));

            subEntityResults.push({
              entityName: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
              subEntityFields,
              parentFields,
              data: filteredData,
              availability,
              subEntityFieldKeys,
            });
          }
        }
      });

      return subEntityResults.length > 0 ? subEntityResults : null;
    }

    return null;
  };

  useEffect(() => {
    if (containerRef?.current) {
      containerRef?.current.scrollTo(0, 0);
    }
  }, [displayAvailabilities]);

  const getAvailabilityAccordionKey = (availability: any, index: number) =>
    `${availability?.id || availability?.uuid || availability?.order || index}`;

  useEffect(() => {
    const firstExpandableAvailability = displayAvailabilities.find(
      (availability) => {
        const subEntityData = getSubEntityData(availability, displayFields);
        if (!subEntityData || subEntityData.length === 0) {
          return false;
        }

        const floorSpecificationsEntity = subEntityData.find((entityData: any) =>
          `${entityData?.entityName || ''}`
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '')
            .includes('floorspecification'),
        );

        if (!floorSpecificationsEntity) {
          return true;
        }

        return (floorSpecificationsEntity?.data?.length || 0) > 1;
      },
    );

    setExpandedAvailabilityKey(
      firstExpandableAvailability
        ? getAvailabilityAccordionKey(
          firstExpandableAvailability,
          displayAvailabilities.indexOf(firstExpandableAvailability),
        )
        : null,
    );
  }, [displayAvailabilities, displayFields]);

  const emptyCells = (keyPrefix: string) => {
    const width = window.innerWidth;
    let numEmptyCol = 6 - displayColumnCount;
    if (width <= 768) {
      numEmptyCol = Math.floor((width - 188) / 94) - displayColumnCount;
    } else if (width <= 1024) {
      const value = Math.floor((width - 188) / 142);

      numEmptyCol = (value > 6 ? 6 : value) - displayColumnCount;
    }

    return numEmptyCol > 0
      ? [...Array(numEmptyCol)].map((item, i) => (
        <TableCell
          key={`${keyPrefix}-empty-${i}`}
          component="th"
          classes={{ root: classes.noBottomBorder }}
          className="empty-cell"
        />
      ))
      : null;
  };
  const renderLinks = (value: string) => {
    // Split by comma first, then process each part
    const parts = value.split(',').map((part) => part.trim()).filter((part) => part.length > 0);

    return parts.map((item: string, idx: number) => {
      // Match label - URL pattern, allowing for various URL formats
      const labelUrlMatch = item.match(/^(.*?)\s*-\s*((?:https?:\/\/)?[\w.-]+(?:\.[\w]+)+(?:\/[^\s]*)?)$/);

      if (labelUrlMatch) {
        const label = labelUrlMatch[1]?.trim() || '';
        let url = labelUrlMatch[2]?.trim() || '';
        // Add protocol if missing
        if (url && !url.startsWith('http')) {
          url = `https://${url}`;
        }

        return (
          <React.Fragment key={`link-${idx}`}>
            {label ? `${label} - ` : ''}
            {url && (
              <Link
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.link}
              >
                {labelUrlMatch[2]} {/* Show original URL format */}
              </Link>
            )}
            {idx < parts.length - 1 && <br />} {/* Add break except for last item */}
          </React.Fragment>
        );
      }

      // Check if the item contains both text and URL (like "Test 1 www.google.com")
      const textUrlMatch = item.match(/^(.*?)\s+((?:https?:\/\/)?[\w.-]+(?:\.[\w]+)+(?:\/[^\s]*)?)$/);
      if (textUrlMatch) {
        const text = textUrlMatch[1]?.trim() || '';
        let url = textUrlMatch[2]?.trim() || '';
        // Add protocol if missing
        if (url && !url.startsWith('http')) {
          url = `https://${url}`;
        }

        return (
          <React.Fragment key={`text-url-${idx}`}>
            {text}{' '}
            <Link
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.link}
            >
              {textUrlMatch[2]} {/* Show original URL format */}
            </Link>
            {idx < parts.length - 1 && <br />}
          </React.Fragment>
        );
      }

      // Check if the item is just a URL (like www.google.com)
      const urlMatch = item.match(/^((?:https?:\/\/)?[\w.-]+(?:\.[\w]+)+(?:\/[^\s]*)?)$/);
      if (urlMatch) {
        let url = urlMatch[1]?.trim() || '';
        // Add protocol if missing
        if (url && !url.startsWith('http')) {
          url = `https://${url}`;
        }

        return (
          <React.Fragment key={`url-${idx}`}>
            <Link
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.link}
            >
              {item} {/* Show original URL format */}
            </Link>
            {idx < parts.length - 1 && <br />}
          </React.Fragment>
        );
      }

      // Fallback for items that don't match any pattern
      return (
        <React.Fragment key={`text-${idx}`}>
          {item}
          {idx < parts.length - 1 && <br />}
        </React.Fragment>
      );
    });
  };

  /**
   * Formats a measurement text string by splitting it into lines,
   * bolding specific area types, and rendering each line as a Typography element.
   * Returns a dash if the value is empty or '-'.
   * @param value - The measurement text to format and render.
   */
  const renderFormattedMeasurementText = (value: string) => {
    if (!value || value === '-') {
      return <Typography variant="body1">-</Typography>;
    }

    // Split by newlines to process each line
    const lines = value.split('\n');
    // eslint-disable-next-line no-undef
    const formattedLines: JSX.Element[] = [];

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return;

      // Define area types that should be bold
      const areaTypes = [
        'Mezzanine Area',
        'Warehouse Area',
        'Office Area',
      ];

      // Check if line contains any area type that should be bold
      const matchingAreaType = areaTypes.find((areaType) =>
        trimmedLine.toLowerCase().includes(areaType.toLowerCase()),
      );

      if (matchingAreaType) {
      // Find the exact match (case-insensitive) and replace with bold version
        const regex = new RegExp(`(${matchingAreaType})`, 'gi');
        const parts = trimmedLine.split(regex);

        formattedLines.push(
        <div key={`line-${index}`}>
          <Typography variant="body1">
            {parts.map((part, partIndex) => {
              if (part.toLowerCase() === matchingAreaType.toLowerCase()) {
                return <strong key={partIndex}>{part}</strong>;
              }
              return part;
            })}
          </Typography>
        </div>,
        );
      } else {
        formattedLines.push(
        <Typography key={`line-${index}`} variant="body1">
          {trimmedLine}
        </Typography>,
        );
      }
    });

    return <div>{formattedLines}</div>;
  };

  const resolveRowVariant = (
    subEntityData: any[] | null,
    shouldRenderExpandableSubEntity: boolean,
  ): ResolvedAvailabilityTableVariant => {
    if (tableVariant === 'simple') {
      return 'simple';
    }

    if (tableVariant === 'subEntity') {
      return subEntityData && shouldRenderExpandableSubEntity
        ? 'subEntity'
        : 'simple';
    }

    return subEntityData && shouldRenderExpandableSubEntity
      ? 'subEntity'
      : 'simple';
  };

  const shouldRenderExpandableSubEntityRow = (subEntityData: any[] | null) => {
    if (!subEntityData || subEntityData.length === 0) {
      return false;
    }

    const floorSpecificationsEntity = subEntityData.find((entityData: any) =>
      `${entityData?.entityName || ''}`
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
        .includes('floorspecification'),
    );

    if (!floorSpecificationsEntity) {
      return true;
    }

    return (floorSpecificationsEntity?.data?.length || 0) > 1;
  };

  const renderAvailabilityValue = (
    column: any,
    rawValue: string | undefined,
    fallback: string = '-',
  ) => {
    const value = (rawValue || '').trim();
    const isLink =
      ((surveyCountry_PAC && linkAttributes_pac.includes(column.fieldLabel)) ||
        (surveyCountry_UK &&
          linkAttributes_UK_Regional.includes(column.fieldLabel)) ||
        (!surveyCountry_PAC && linkAttributes.includes(column.fieldLabel))) &&
      !!value;
    const isMeasurement =
      Measurement.includes(column.fieldLabel) && surveyCountry_CE;
    const isHTML = /<[a-z][\s\S]*>/i.test(value);

    if (isLink) {
      return renderLinks(value);
    }

    if (isMeasurement) {
      return renderFormattedMeasurementText(value || fallback);
    }

    if (isHTML) {
      const safeHtml = DOMPurify.sanitize(String(value), {
        USE_PROFILES: { html: true },
      });
      return (
        <Typography
          variant="body1"
          dangerouslySetInnerHTML={{ __html: safeHtml }}
          className={classes.contentStyle}
        />
      );
    }

    if (isAvailabilityDescriptionField(column?.fieldKey)) {
      return (
        <ExpandableText
          text={value}
          collapsedLines={3}
          fallbackText={fallback}
          expandLabel={t('propertyDetail.viewMore')}
          collapseLabel={t('propertyDetail.showLess')}
        />
      );
    }

    return <Typography variant="body1">{value || fallback}</Typography>;
  };

  const getStatusChipClass = (value: string) => {
    const normalizedValue = `${value || ''}`.toLowerCase();

    if (normalizedValue.includes('immediate')) {
      return `${classes.statusChip} ${classes.statusChipAvailable}`;
    }

    if (normalizedValue.includes('confirm') || normalizedValue.includes('tbc')) {
      return `${classes.statusChip} ${classes.statusChipPending}`;
    }

    if (/q[1-4]|20\d{2}|future|available/.test(normalizedValue)) {
      return `${classes.statusChip} ${classes.statusChipFuture}`;
    }

    return '';
  };

  const isFloorPlanColumn = (column: any) => {
    const identity = `${column?.fieldKey || ''} ${column?.fieldLabel || ''}`
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
    return identity.includes('floorplan') || identity.includes('floorplans');
  };

  const getFloorPlanHref = (value: string) => {
    const match = value.match(/https?:\/\/[^\s,]+/i);
    if (match) {
      return match[0];
    }

    return /^www\./i.test(value) ? `https://${value}` : '';
  };

  const getFloorPlanMediaHref = (floorPlan?: AvailabilityFloorPlan) =>
    floorPlan?.downloadUrl || floorPlan?.pdf || floorPlan?.url || '';

  const FloorPlanActionIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 2.75 13 5.75 8 8.75 3 5.75 8 2.75Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M4.45 8.1 3 8.95l5 3 5-3-1.45-.85" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const getFloorPlanDisplayTitle = (
    floorPlan: AvailabilityFloorPlan | undefined,
    index: number,
  ) => floorPlan?.displayCaption || floorPlan?.displayName || t('media.floorPlanWithIndex', { index: index + 1 });

  const openFloorPlanCarousel = (availabilityIndex: number) => {
    if (!hasFloorPlans) {
      return;
    }

    setFloorPlanCarouselIndex(Math.min(availabilityIndex, floorPlans.length - 1));
    setIsFloorPlanCarouselOpen(true);
  };

  const changeFloorPlanCarouselIndex = (direction: 'previous' | 'next') => {
    setFloorPlanCarouselIndex((currentIndex) => {
      if (direction === 'previous') {
        return currentIndex === 0 ? floorPlans.length - 1 : currentIndex - 1;
      }

      return currentIndex === floorPlans.length - 1 ? 0 : currentIndex + 1;
    });
  };

  const renderFloorPlanCell = (availabilityIndex: number, borderTop?: string) => (
    <TableCell
      className={classes.noBottomBorder}
      component="th"
      key={`floor-plan-${availabilityIndex}-cell`}
      style={{
        verticalAlign: 'middle',
        borderTop,
      }}
    >
      {hasFloorPlans ? (
        <button
          type="button"
          className={classes.floorPlanAction}
          onClick={() => openFloorPlanCarousel(availabilityIndex)}
        >
          <FloorPlanActionIcon />
          {t('availabilities.floorPlanAction')}
        </button>
      ) : (
        <Typography variant="body1">-</Typography>
      )}
    </TableCell>
  );

  const renderAvailabilityDisplayValue = (column: any, value: string | undefined) => {
    const displayValue = (value || '').trim();
    const chipClass = getStatusChipClass(displayValue);

    if (isFloorPlanColumn(column) && displayValue && displayValue !== '-') {
      const href = getFloorPlanHref(displayValue);

      if (href) {
        return (
          <Link
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.floorPlanAction}
          >
            <FloorPlanActionIcon />
            {t('availabilities.floorPlanAction')}
          </Link>
        );
      }
    }

    if (chipClass && !isAvailabilityDescriptionField(column?.fieldKey)) {
      return <span className={chipClass}>{displayValue}</span>;
    }

    return renderAvailabilityValue(column, value);
  };

  const renderFloorPlanPreview = (
    activeFloorPlan: AvailabilityFloorPlan | undefined,
    href: string,
    isPdf: boolean,
  ) => {
    const title = getFloorPlanDisplayTitle(activeFloorPlan, floorPlanCarouselIndex);

    if (!href) {
      return <Typography variant="body1">{t('common.notAvailable')}</Typography>;
    }

    if (isPdf) {
      return (
        <object
          data={href}
          type="application/pdf"
          className={classes.floorPlanPreviewPdf}
          aria-label={title}
        >
          {title}
        </object>
      );
    }

    return (
      <img
        src={href}
        className={classes.floorPlanPreviewImage}
        alt={title}
      />
    );
  };

  const renderFloorPlanCarousel = () => {
    const activeFloorPlan = floorPlans[floorPlanCarouselIndex];
    const href = getFloorPlanMediaHref(activeFloorPlan);
    const isPdf = Boolean(activeFloorPlan?.pdf) || /\.pdf(?:\?|$)/i.test(href);
    const hasMultipleFloorPlans = floorPlans.length > 1;

    return (
      <Dialog
        fullWidth
        maxWidth="md"
        open={isFloorPlanCarouselOpen}
        onClose={() => setIsFloorPlanCarouselOpen(false)}
        aria-labelledby="floor-plan-carousel-title"
      >
        <Box className={classes.floorPlanCarouselHeader}>
          <Typography
            id="floor-plan-carousel-title"
            className={classes.floorPlanCarouselTitle}
          >
            {getFloorPlanDisplayTitle(activeFloorPlan, floorPlanCarouselIndex)}
          </Typography>
          <IconButton
            aria-label="Close floor plan"
            onClick={() => setIsFloorPlanCarouselOpen(false)}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent className={classes.floorPlanCarouselContent}>
          <Box className={classes.floorPlanPreviewFrame}>
            {renderFloorPlanPreview(activeFloorPlan, href, isPdf)}
          </Box>
          {hasMultipleFloorPlans && (
            <Box className={classes.floorPlanCarouselControls}>
              <IconButton
                aria-label="Previous floor plan"
                onClick={() => changeFloorPlanCarouselIndex('previous')}
              >
                <ChevronLeftIcon />
              </IconButton>
              <Typography className={classes.floorPlanCarouselCount}>
                {floorPlanCarouselIndex + 1}/{floorPlans.length}
              </Typography>
              <IconButton
                aria-label="Next floor plan"
                onClick={() => changeFloorPlanCarouselIndex('next')}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  const renderAvailabilityToggle = (
    isExpanded = false,
    onToggle?: () => void,
  ) => (
    <button
      type="button"
      className={classes.availabilityAccordionToggle}
      onClick={onToggle}
      aria-expanded={isExpanded}
    >
      <svg
        className={`${classes.availabilityChevron} ${
          isExpanded ? classes.availabilityChevronExpanded : ''
        }`}
        viewBox="0 0 20 20"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="m8 6 4 4-4 4"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );

  const renderAvailabilityName = (
    availability: any,
    hasNestedRows = false,
    isExpanded = false,
    onToggle?: () => void,
  ) => (
    <Box className={classes.availabilityNameCell}>
      {hasNestedRows && renderAvailabilityToggle(isExpanded, onToggle)}
      <Typography variant="body1">
        {isCESurvey
          ? getAvailabilityNameValueLocal(availability)
          : getAvailabilityFieldValues(AVAILABILITY_NAME_KEY, availability)}
      </Typography>
    </Box>
  );

  const getNestedFieldHeader = (field: any, entityName: string) => {
    const header = field.fieldLabel || field.fieldKey || field.field;
    const prefix = `${entityName || ''}`.trim();

    if (!prefix || !header) {
      return header;
    }

    return header.toLowerCase().startsWith(prefix.toLowerCase())
      ? header.slice(prefix.length).trim() || header
      : header;
  };

  const normalizeFieldIdentity = (value?: string) =>
    `${value || ''}`.toLowerCase().replace(/[^a-z0-9]/g, '');

  const getFieldKey = (field: any) => field?.fieldKey || field?.field || '';

  const getMatchedNestedField = (column: any, entityData: any) => {
    const nestedFields = entityData?.subEntityFields || [];
    const columnIdentities = [
      column?.fieldKey,
      column?.field,
      column?.fieldLabel,
      getColumnHeader(t, i18n.language, column?.fieldKey, column?.fieldLabel),
    ].map(normalizeFieldIdentity).filter(Boolean);

    return nestedFields.find((nestedField: any) => {
      const nestedIdentities = [
        nestedField?.fieldKey,
        nestedField?.field,
        nestedField?.fieldLabel,
        getNestedFieldHeader(nestedField, entityData?.entityName),
      ].map(normalizeFieldIdentity).filter(Boolean);

      return nestedIdentities.some((nestedIdentity: string) =>
        columnIdentities.includes(nestedIdentity),
      );
    });
  };

  const getPrimaryNestedEntity = (subEntityData: any[] | null) =>
    subEntityData?.find((entityData: any) => entityData?.data?.length > 0);

  const getNestedDisplayValue = (column: any, entityData: any, row: any) => {
    const matchedNestedField = getMatchedNestedField(column, entityData);
    const matchedFieldKey = getFieldKey(matchedNestedField);

    if (!matchedFieldKey) {
      return undefined;
    }

    return formatNumericValue(row?.[matchedFieldKey], matchedFieldKey) || '-';
  };

  const getNestedFieldDisplayOrder = (field: any, entityName: string) => {
    const identity = normalizeFieldIdentity(getNestedFieldHeader(field, entityName));

    if (identity.includes('floor')) return 0;
    if (identity.includes('suite')) return 1;
    if (identity.includes('spacesize') || identity.includes('size')) return 2;
    if (identity.includes('spacetype')) return 3;
    if (identity.includes('usage')) return 4;

    return 100;
  };

  const getOrderedNestedFields = (fields: any[], entityName: string) =>
    [...fields].sort((firstField: any, secondField: any) => {
      const firstOrder = getNestedFieldDisplayOrder(firstField, entityName);
      const secondOrder = getNestedFieldDisplayOrder(secondField, entityName);

      if (firstOrder !== secondOrder) {
        return firstOrder - secondOrder;
      }

      return (firstField?.order || 0) - (secondField?.order || 0);
    });

  const renderNestedTable = (entityData: any, availabilityIndex: number, startIndex = 0) => {
    const nestedFields = getOrderedNestedFields(
      entityData.subEntityFields || [],
      entityData.entityName,
    );
    const nestedRows = (entityData.data || []).slice(startIndex);

    if (nestedFields.length === 0 || nestedRows.length === 0) {
      return null;
    }

    return (
      <TableRow
        className={classes.nestedWrapperRow}
        key={`nested-table-${availabilityIndex}-${entityData.entityName}`}
      >
        <TableCell
          className={classes.nestedWrapperCell}
          colSpan={displayColumnCount + (isIncludeAvailabilityName ? 1 : 0)}
        >
          <Box className={classes.nestedPanel}>
            <table className={classes.nestedTable}>
              <thead>
                <tr>
                  {nestedFields.map((field: any) => (
                    <th key={`${entityData.entityName}-${field.fieldKey || field.field}`}>
                      {getNestedFieldHeader(field, entityData.entityName)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {nestedRows.map((dataRow: any, rowIndex: number) => (
                  <tr key={`${entityData.entityName}-row-${rowIndex}`}>
                    {nestedFields.map((field: any) => {
                      const fieldKey = field.fieldKey || field.field;
                      return (
                        <td key={`${entityData.entityName}-${rowIndex}-${fieldKey}`}>
                          {formatNumericValue(dataRow[fieldKey], fieldKey) || '-'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  return displayAvailabilities?.length > 0 ? (
    <>
      {renderFloorPlanCarousel()}
      <Typography variant="h6" className={classes.availabilitiesText}>
        {t('availabilities.title')}
      </Typography>

      <Paper className={classes.paperRoot}>
        <TableContainer ref={containerRef} style={{ width: '100%', overflowX: 'auto' }}>
          <Table
            stickyHeader
            aria-label="sticky table"
            className={classes.tableRoot}
            style={{ tableLayout: 'auto' }}
          >
            <TableHead>
              <TableRow className={classes.tableHeader}>
                {
                  isIncludeAvailabilityName && <TableCell
                    component="th"
                    key={'availabilityName'}
                    className={`${classes.noBottomBorder} ${classes.stickyColumnHeader}`}
                    style={{ left: 0, zIndex: 3 }}
                  >
                    <Typography variant="body1"> {availabilityNameHeader} </Typography>
                  </TableCell>
                }
                {displayFields.map((column: any) => (
                  <TableCell
                    component="th"
                    key={column.fieldKey}
                    className={classes.noBottomBorder}
                  >
                   <Typography variant="body1">{getColumnHeader(t, i18n.language, column.fieldKey, column.fieldLabel)}</Typography>
                  </TableCell>
                ))}
                {hasFloorPlans && (
                  <TableCell
                    component="th"
                    key="floorPlan"
                    className={classes.noBottomBorder}
                  >
                    <Typography variant="body1">{t('media.floorPlan')}</Typography>
                  </TableCell>
                )}
                {emptyCells('table-header')}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayAvailabilities.map((row: any, index) => {
                if (!row) {
                  return null;
                }

                const subEntityData = getSubEntityData(row, displayFields);
                const shouldRenderExpandableSubEntity =
                  shouldRenderExpandableSubEntityRow(subEntityData);
                const rowVariant = resolveRowVariant(
                  subEntityData,
                  shouldRenderExpandableSubEntity,
                );
                const primaryNestedEntity = getPrimaryNestedEntity(subEntityData);
                const primaryNestedRow = primaryNestedEntity?.data?.[0];
                const availabilityAccordionKey = getAvailabilityAccordionKey(row, index);
                const isAvailabilityExpanded =
                  expandedAvailabilityKey === availabilityAccordionKey;
                const toggleAvailabilityExpansion = () => {
                  setExpandedAvailabilityKey((currentKey) =>
                    currentKey === availabilityAccordionKey
                      ? null
                      : availabilityAccordionKey,
                  );
                };

                return (
                  <React.Fragment key={`availability-fragment-${index}`}>
                    {rowVariant === 'simple' && (
                      <TableRow className={classes.root} tabIndex={-1} key={row.id}>
                        {isIncludeAvailabilityName && (
                          <TableCell
                            component="th"
                            className={`${classes.noBottomBorder} ${classes.stickyColumn}`}
                            key={`${index}-availabilityName-cell`}
                            style={{
                              verticalAlign: 'middle',
                              zIndex: 3,
                              borderTop: index > 0 ? '2px solid #DFE6E5' : undefined,
                            }}
                          >
                            <Typography variant="body1">
                              {isCESurvey ? getAvailabilityNameValueLocal(row) :
                                getAvailabilityFieldValues(AVAILABILITY_NAME_KEY, row)}
                            </Typography>
                          </TableCell>
                        )}
                        {displayFields.map((column: any) => {
                          const value = getAvailabilityFieldValues(
                            column.fieldKey,
                            row,
                          );
                          const nestedDisplayValue = primaryNestedRow
                            ? getNestedDisplayValue(
                              column,
                              primaryNestedEntity,
                              primaryNestedRow,
                            )
                            : undefined;
                          const cellValue = nestedDisplayValue !== undefined
                            ? <Typography variant="body1">{nestedDisplayValue}</Typography>
                            : renderAvailabilityDisplayValue(column, value);
                          return (
                            <TableCell
                              className={classes.noBottomBorder}
                              component="th"
                              key={`${column.fieldKey}-cell`}
                              style={{
                                verticalAlign: 'middle',
                                borderTop: index > 0 ? '2px solid #DFE6E5' : undefined,
                              }}
                            >
                              {cellValue}
                            </TableCell>
                          );
                        })}
                        {hasFloorPlans && renderFloorPlanCell(index, index > 0 ? '2px solid #DFE6E5' : undefined)}
                        {emptyCells(`${index}-table-content`)}
                      </TableRow>
                    )}

                    {rowVariant === 'subEntity' && subEntityData && (
                      <>
                        <TableRow className={classes.nestedParentRow} tabIndex={-1} key={row.id}>
                          {isIncludeAvailabilityName && (
                            <TableCell
                              component="th"
                              className={`${classes.noBottomBorder} ${classes.stickyColumn}`}
                              style={{
                                verticalAlign: 'middle',
                                zIndex: 3,
                                borderTop: index > 0 ? '2px solid #DFE6E5' : undefined,
                              }}
                            >
                              {renderAvailabilityName(
                                row,
                                true,
                                isAvailabilityExpanded,
                                toggleAvailabilityExpansion,
                              )}
                            </TableCell>
                          )}
                          {displayFields.map((column: any, fieldIndex: number) => {
                            const value = getAvailabilityFieldValues(
                              column.fieldKey,
                              row,
                            );
                            const nestedDisplayValue = primaryNestedRow
                              ? getNestedDisplayValue(column, primaryNestedEntity, primaryNestedRow)
                              : undefined;
                            const cellValue = nestedDisplayValue !== undefined
                              ? <Typography variant="body1">{nestedDisplayValue}</Typography>
                              : renderAvailabilityDisplayValue(column, value);
                            return (
                              <TableCell
                                component="th"
                                className={classes.noBottomBorder}
                                key={`${column.fieldKey}-cell`}
                                style={{
                                  verticalAlign: 'middle',
                                  zIndex: 3,
                                  borderTop: index > 0 ? '2px solid #DFE6E5' : undefined,
                                }}
                              >
                                {!isIncludeAvailabilityName && fieldIndex === 0 ? (
                                  <Box className={classes.availabilityNameCell}>
                                    {renderAvailabilityToggle(
                                      isAvailabilityExpanded,
                                      toggleAvailabilityExpansion,
                                    )}
                                    {cellValue}
                                  </Box>
                                ) : cellValue}
                              </TableCell>
                            );
                          })}
                          {hasFloorPlans && renderFloorPlanCell(index, index > 0 ? '2px solid #DFE6E5' : undefined)}
                          {emptyCells(`${index}-nested-parent-content`)}
                        </TableRow>
                        {isAvailabilityExpanded &&
                          subEntityData.map((entityData: any) =>
                            renderNestedTable(
                              entityData,
                              index,
                              entityData === primaryNestedEntity ? 1 : 0,
                            ),
                          )}
                      </>
                    )}

                    {rowVariant === 'subEntity' && isAvailabilityExpanded && showTotals && subEntityData && subEntityData.length > 0 && (() => {
                      // Use survey property type directly
                      const propertyType = survey?.propertyType?.trim();

                      // Calculate totals for property type and all
                      const propertyTypeTotals = calculateSubEntityTotals(
                        subEntityData,
                        true,
                        propertyType,
                      );
                      const allTotals = calculateSubEntityTotals(subEntityData, false);

                      // Get the first sub-entity to extract unit information
                      const firstEntity = subEntityData[0];
                      const { subEntityFields, allColumns } = firstEntity;

                      // Find the space type field in template columns
                      const spaceTypeFieldDef = (allColumns || subEntityFields).find((field: any) => {
                        const fieldKey = (field.fieldKey || field.field || '').toLowerCase();
                        return fieldKey.includes('space_type') || fieldKey.includes('spacetype');
                      });
                      const spaceTypeFieldKey = spaceTypeFieldDef?.fieldKey || spaceTypeFieldDef?.field;

                      // Check if space type field is in displayFields (visible columns) - same logic as column visibility
                      const isSpaceTypeActive = displayFields.some((field: any) => {
                        const matchedNestedField = getMatchedNestedField(field, firstEntity);
                        return getFieldKey(matchedNestedField) === spaceTypeFieldKey;
                      });
                      const firstSubEntityDisplayFieldIndex = displayFields.findIndex(
                        (field: any) => Boolean(getMatchedNestedField(field, firstEntity)),
                      );

                      // Helper function to render a total row
                      const renderTotalRow = (
                        totals: { [key: string]: number },
                        spaceTypeLabel: string,
                        rowKey: string,
                        isLast: boolean,
                      ) => (
                        <TableRow
                          key={rowKey}
                          style={{
                            backgroundColor: '#F5F7F7',
                            fontWeight: 600,
                            borderTop: '2px solid #DFE6E5',
                            borderBottom: isLast ? '2px solid #DFE6E5' : undefined,
                          }}>
                          {/* Availability name column - show empty */}
                          {isIncludeAvailabilityName && (
                            <TableCell
                              className={`${classes.noBottomBorder} ${classes.stickyColumn}`}
                              style={{
                                fontWeight: 600,
                                fontSize: '16px',
                                zIndex: 3,
                              }}>
                              <Typography
                                variant="body1"
                                style={{ fontWeight: 600 }}>
                                {/* Empty - no label in availability name column */}
                              </Typography>
                            </TableCell>
                          )}

                          {/* Data cells for each field */}
                          {displayFields.map((column: any, fieldIndex: number) => {
                            const matchedNestedField = getMatchedNestedField(column, firstEntity);
                            const matchedFieldKey = getFieldKey(matchedNestedField);
                            const isSubEntityField = Boolean(matchedFieldKey);
                            const totalValue = totals[matchedFieldKey];

                            if (isSubEntityField) {
                              // For the first sub-entity field, show "Total" label
                              if (fieldIndex === firstSubEntityDisplayFieldIndex) {
                                return (
                                  <TableCell
                                    key={`${rowKey}-cell-${fieldIndex}`}
                                    className={classes.noBottomBorder}
                                    style={{
                                      fontWeight: 600,
                                      fontSize: '16px',
                                    }}>
                                    <Typography
                                      variant="body1"
                                      style={{ fontWeight: 600 }}>
                                      {t('availabilities.total')}
                                    </Typography>
                                  </TableCell>
                                );
                              }

                              // For space type field, show the space type label
                              if (spaceTypeFieldKey && matchedFieldKey === spaceTypeFieldKey) {
                                return (
                                  <TableCell
                                    key={`${rowKey}-cell-${fieldIndex}`}
                                    className={classes.noBottomBorder}
                                    style={{
                                      fontWeight: 600,
                                      fontSize: '16px',
                                    }}>
                                    <Typography
                                      variant="body1"
                                      style={{ fontWeight: 600 }}>
                                      {spaceTypeLabel}
                                    </Typography>
                                  </TableCell>
                                );
                              }

                              // For numeric fields with totals, show the total value
                              if (totalValue !== undefined) {
                                // Extract unit from the field (look at first data row)
                                const firstDataRow = firstEntity.data[0];
                                const firstValue = firstDataRow?.[matchedFieldKey] || '';
                                const unit = extractUnit(firstValue);

                                // Format number with comma separators
                                // Only show decimals if the value is not an integer
                                const isInteger = Number.isInteger(totalValue);
                                const formattedValue = totalValue.toLocaleString('en-US', {
                                  minimumFractionDigits: isInteger ? 0 : 2,
                                  maximumFractionDigits: 2,
                                });

                                return (
                                  <TableCell
                                    key={`${rowKey}-cell-${fieldIndex}`}
                                    className={classes.noBottomBorder}
                                    style={{
                                      fontWeight: 600,
                                      fontSize: '16px',
                                    }}>
                                    <Typography
                                      variant="body1"
                                      style={{ fontWeight: 600 }}>
                                      {`${formattedValue} ${unit}`.trim()}
                                    </Typography>
                                  </TableCell>
                                );
                              }

                              // For non-numeric sub-entity fields, show empty cell
                              return (
                                <TableCell
                                  key={`${rowKey}-cell-${fieldIndex}`}
                                  className={classes.noBottomBorder}
                                  style={{
                                    fontSize: '16px',
                                  }}>
                                  <Typography variant="body1">
                                    -
                                  </Typography>
                                </TableCell>
                              );
                            }

                            // For parent fields, show empty cell (no dash)
                            return (
                              <TableCell
                                key={`${rowKey}-cell-${fieldIndex}`}
                                className={classes.noBottomBorder}
                                style={{
                                  backgroundColor: '#FEFEFE',
                                  fontSize: '16px',
                                }}>
                                <Typography variant="body1">
                                  {/* Empty - no content */}
                                </Typography>
                              </TableCell>
                            );
                          })}
                          {hasFloorPlans && (
                            <TableCell
                              key={`${rowKey}-floor-plan-total-cell`}
                              className={classes.noBottomBorder}
                              style={{
                                backgroundColor: '#FEFEFE',
                                fontSize: '16px',
                              }}
                            >
                              <Typography variant="body1" />
                            </TableCell>
                          )}
                          {/* Add empty cells to match the table width */}
                          {emptyCells(`${rowKey}-totals`)}
                        </TableRow>
                      );

                      return (
                        <>
                          {/* Property type total row - only show if space type field is active */}
                          {propertyType && isSpaceTypeActive && renderTotalRow(
                            propertyTypeTotals,
                            propertyType,
                            `total-property-type-${index}`,
                            false,
                          )}
                          {/* All space types total row */}
                          {renderTotalRow(
                            allTotals,
                            t('availabilities.all'),
                            `total-all-${index}`,
                            true,
                          )}
                        </>
                      );
                    })()}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  ) : null;
};

export default PropertyAvailabilities;
